import { Component, OnDestroy, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { Subscription } from 'rxjs'
import { AppState } from '../core/store'
import { selectAuthState } from '../core/store/auth/auth.selectors'
import { RoleGuard } from '../guards/role-guard.service'
import { NAV_ITEMS } from '../nav-items.const'
import { flattenEnabledNavItems, HomeShortcut } from '../nav-items.util'
import { RecentSection, RecentSectionsService } from './recent-sections.service'

export interface DisplayShortcut extends HomeShortcut {
  placeholder?: boolean
}

export interface ShortcutSection {
  groupName: string
  shortcuts: DisplayShortcut[]
}

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
})
export class HomePageComponent implements OnInit, OnDestroy {
  username: string | null = null
  loaded = false
  // One section per top-level/sub-group, in NAV_ITEMS order: MI, GSE,
  // NPI, Modeling, Credit Admin — each with its own heading, stacked in
  // a single column.
  sections: ShortcutSection[] = []
  recents: RecentSection[] = []

  private authSubscription: Subscription

  constructor(
    private store: Store<AppState>,
    private roleGuard: RoleGuard,
    private recentSectionsService: RecentSectionsService,
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.store.pipe(select(selectAuthState)).subscribe((authState) => {
      this.username = authState.name

      if (!authState.userPermissions || !authState.security) {
        return
      }

      const allowedGuards = this.roleGuard.checkPermissions(
        authState.userPermissions,
        authState.security,
      )
      const shortcuts = flattenEnabledNavItems(NAV_ITEMS, allowedGuards)

      this.sections = this.groupByGroupName(shortcuts)
      this.recents = this.recentSectionsService
        .getRecents()
        .filter((recent) => allowedGuards.has(recent.route))
      this.loaded = true
    })
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe()
  }

  private groupByGroupName(shortcuts: HomeShortcut[]): ShortcutSection[] {
    const groups = new Map<string, HomeShortcut[]>()
    shortcuts.forEach((shortcut) => {
      const group = groups.get(shortcut.groupName) ?? []
      group.push(shortcut)
      groups.set(shortcut.groupName, group)
    })
    return Array.from(groups.entries()).map(([groupName, groupShortcuts]) => ({
      groupName,
      shortcuts: groupShortcuts,
    }))
  }
}
